import React, { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useReducedMotion from '../hooks/useReducedMotion';
import ux from '../assets/services/ux.webp';
import brand from '../assets/services/brand.webp';
import web from '../assets/services/web.webp';
import three from '../assets/services/3d.webp';
import game from '../assets/services/game.webp';
import './services.css';

const services = [
  { title: 'UX & UI Design', image: ux, detail: 'Research, wireframes and prototypes that turn human needs into intuitive digital experiences.', tags: 'RESEARCH / INTERACTION / PROTOTYPING' },
  { title: 'Brand & Graphic Design', image: brand, detail: 'Distinctive identities, expressive visuals and consistent design systems that give your story a clear voice.', tags: 'IDENTITY / EDITORIAL / VISUAL SYSTEMS' },
  { title: 'Websites & Web Apps', image: web, detail: 'Responsive websites and interfaces that connect thoughtful design with functional frontend development.', tags: 'REACT / RESPONSIVE DESIGN / DEVELOPMENT' },
  { title: '3D Design', image: three, detail: 'Characters, objects and visual worlds that bring another dimension to your ideas.', tags: 'MODELLING / VISUALISATION / STORYTELLING' },
  { title: 'Games & Interactive Experiences', image: game, detail: 'Playful, meaningful experiences built around exploration, emotion and connection.', tags: 'PLAY / INTERACTION / EXPERIENCE DESIGN' },
];

export default function Services() {
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const animateRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const reduced = useReducedMotion();
  useLayoutEffect(() => {
    const scope = root.current;
    const context = gsap.context(() => {}, root);
    context.add('select', (active) => {
      scope.querySelectorAll('.services__art').forEach((image, index) => {
        gsap.to(image, { autoAlpha: index === active ? 1 : 0, y: index === active ? 0 : 28,
          rotation: index === active ? -3 : 7, scale: index === active ? 1 : .87,
          duration: reduced ? 0 : .65, ease: 'power3.out', overwrite: true });
      });
      scope.querySelectorAll('.service-row').forEach((row, index) => {
        gsap.to(row.querySelector('.service-row__wash'), { scaleX: index === active ? 1 : 0, duration: reduced ? 0 : .6, ease: 'power3.inOut', overwrite: true });
        gsap.to(row.querySelector('.service-row__detail'), { height: index === active ? 'auto' : 0, opacity: index === active ? 1 : 0, duration: reduced ? 0 : .5, ease: 'power3.inOut', overwrite: true,
          onComplete: index === active ? () => ScrollTrigger.refresh() : undefined });
      });
    });
    animateRef.current = context.select;
    context.select(activeRef.current);
    return () => { animateRef.current = null; context.revert(); };
  }, [reduced]);
  useLayoutEffect(() => { animateRef.current?.(active); }, [active]);

  return <section className="services section-shell" id="services" ref={root} aria-labelledby="services-title">
    <div className="section-heading" data-reveal><span className="eyebrow">01 / WHAT I CAN HELP WITH</span><h2 id="services-title" tabIndex="-1">One curious mind.<br /><em>Many possibilities.</em></h2></div>
    <div className="services__layout">
      <div className="services__stage" data-reveal aria-hidden="true">
        <div className="services__orbit" /><span className="services__cross">✳︎</span>
        {services.map((service, index) => <img className="services__art" src={service.image} alt="" loading="lazy" key={service.title} style={{ opacity: index === 0 ? 1 : 0 }} />)}
        <span className="services__caption">A LITTLE STRATEGY. A LOT OF POSSIBILITY.</span>
      </div>
      <div className="services__list" data-reveal>
        {services.map((service, index) => <article className={`service-row${index === active ? ' is-active' : ''}`} key={service.title} onPointerEnter={(event) => { if (event.pointerType === 'mouse') setActive(index); }}>
          <span className="service-row__wash" aria-hidden="true" />
          <h3><button type="button" onFocus={() => setActive(index)} onClick={() => setActive(index)} aria-expanded={index === active} aria-controls={`service-detail-${index}`}>
            <span className="service-row__number">0{index + 1}</span><span>{service.title}</span><span className="service-row__arrow" aria-hidden="true">↗︎</span>
          </button></h3>
          <div className="service-row__detail" id={`service-detail-${index}`} aria-hidden={index !== active}><div><p>{service.detail}</p><span>{service.tags}</span></div></div>
        </article>)}
      </div>
    </div>
  </section>;
}
